import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { Key } from 'ts-key-enum';

@Component({
  selector: 'crm-galleria',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './galleria.component.html',
  styleUrls: ['./galleria.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class GalleriaComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('fullEl', { static: true }) public fullEl: ElementRef;
  @ViewChild('imageElement') public imageElement: ElementRef;
  @ViewChild('toolboxElement') public toolboxElement: ElementRef;

  private backElement: ElementRef;
  @ViewChild('backElement') public set contentBackElement(content: ElementRef) {
    if (content) {
      this.backElement = content;
    }
  }

  private nextElement: ElementRef;
  @ViewChild('nextElement') public set contentNextElement(content: ElementRef) {
    if (content) {
      this.nextElement = content;
    }
  }

  public readonly MAX_SCALE = 1.5;
  public readonly MIN_SCALE = 0.6;
  public readonly TIME_LOAD_IMAGE = 300;

  public isAnimatedFullShow = false;
  public isFullShow = false;
  public scale = 1.0;
  public rotate = 0;

  @Input() public visible: boolean;
  @Output() public visibleChange = new EventEmitter<boolean>();

  @Input() public activeImageIndex = 0;
  @Input() public images: { id: string; src: string; alt: string }[] = [];
  @Input() public appendTo = 'body';

  /** Snapshot of images while fullscreen is open — ignores live @Input() images changes. */
  public displayImages: { id: string; src: string; alt: string; }[] = [];

  private documentKeydownUnlisten?: () => void;
  private documentClickUnlisten?: () => void;

  public constructor(private readonly renderer: Renderer2) {}

  public get isShowNext(): boolean {
    return this.activeImageIndex + 1 !== this.displayImages?.length;
  }

  public get isShowBack(): boolean {
    return !!this.activeImageIndex;
  }

  public ngOnInit(): void {
    if (this.appendTo === 'body' && this.fullEl) {
      document.body.appendChild(this.fullEl.nativeElement);
    }
    if (this.visible) {
      this.onShowImage();
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.appendTo?.currentValue && this.appendTo === 'body' && this.fullEl) {
      document.body.appendChild(this.fullEl.nativeElement);
    }
    if (changes.visible?.currentValue) {
      this.onShowImage();
    }
    if (changes.visible && !changes.visible.currentValue) {
      this.onHideImage();
    }
  }

  public ngOnDestroy(): void {
    this.unbindDocumentListeners();
  }

  public keyDown(event: KeyboardEvent) {
    if (event.key === Key.Escape && !this.isAnimatedFullShow) {
      this.onHideImage();
    }
    if (event.key === Key.ArrowRight && !this.isAnimatedFullShow && this.isShowNext) {
      this.nextImage();
    }
    if (event.key === Key.ArrowLeft && !this.isAnimatedFullShow && this.isShowBack) {
      this.backImage();
    }
  }

  public onClick(event: PointerEvent) {
    if (this.isAnimatedFullShow) {
      return;
    }

    if (
      !(this.imageElement?.nativeElement as HTMLElement)?.contains(event.target as any) &&
      !(this.toolboxElement?.nativeElement as HTMLElement)?.contains(event.target as any) &&
      !(this.backElement?.nativeElement as HTMLElement)?.contains(event.target as any) &&
      !(this.nextElement?.nativeElement as HTMLElement)?.contains(event.target as any)
    ) {
      this.onHideImage();
    }
  }

  public onShowImage() {
    this.displayImages = (this.images ?? []).map((image) => ({ ...image }));
    this.isAnimatedFullShow = true;
    this.isFullShow = true;
    this.bindDocumentListeners();
    setTimeout(() => {
      this.isAnimatedFullShow = false;
    }, this.TIME_LOAD_IMAGE);
  }

  public onHideImage() {
    if (!this.isFullShow) {
      return;
    }

    this.unbindDocumentListeners();
    this.isAnimatedFullShow = true;
    this.isFullShow = false;
    setTimeout(() => {
      this.scale = 1.0;
      this.rotate = 0;
      this.isAnimatedFullShow = false;
      this.displayImages = [];
      this.visible = false;
      this.visibleChange.emit(false);
    }, this.TIME_LOAD_IMAGE);
  }

  private bindDocumentListeners(): void {
    if (this.documentKeydownUnlisten) {
      return;
    }

    this.documentKeydownUnlisten = this.renderer.listen('document', 'keydown', (event: Event) => {
      this.keyDown(event as KeyboardEvent);
    });
    this.documentClickUnlisten = this.renderer.listen('document', 'click', (event: Event) => {
      this.onClick(event as PointerEvent);
    });
  }

  private unbindDocumentListeners(): void {
    this.documentKeydownUnlisten?.();
    this.documentKeydownUnlisten = undefined;
    this.documentClickUnlisten?.();
    this.documentClickUnlisten = undefined;
  }

  public nextImage() {
    if (this.activeImageIndex + 1 !== this.displayImages?.length) {
      this.activeImageIndex++;
    }
  }

  public backImage() {
    if (this.activeImageIndex) {
      this.activeImageIndex--;
    }
  }

  public plusScale() {
    this.scale += 0.1;
  }

  public minusScale() {
    this.scale -= 0.1;
  }

  public rotateLeft() {
    this.rotate += 90;
  }

  public rotateRight() {
    this.rotate -= 90;
  }
}
